import { apiFetch, apiUrl, useBackendAuth } from './apiClient';
import type { AppSettings } from '../settings/types';

export type XeroInvoiceDisplayStatus = 'paid' | 'overdue' | 'outstanding' | 'draft' | 'void' | 'other';

export type XeroInvoiceRow = {
  id: string;
  number: string;
  contact: string;
  contactId: string;
  contactEmail: string;
  status: string;
  displayStatus: XeroInvoiceDisplayStatus;
  amountDue: number;
  amountPaid: number;
  total: number;
  subTotal: number;
  totalTax: number;
  dueDate: string | null;
  date: string | null;
  dueDateIso: string | null;
  dateIso: string | null;
  currency: string;
  reference: string;
  daysOverdue: number;
  xeroUrl: string | null;
  /** Sent payment reminders logged by this app */
  remindersSent?: number;
};

export type XeroInvoiceDetail = XeroInvoiceRow & {
  lineItems: Array<{
    description: string;
    quantity: number;
    unitAmount: number;
    lineAmount: number;
    accountCode: string;
  }>;
};

export type XeroContactRow = {
  id: string;
  name: string;
  email: string;
  phone: string;
  isCustomer: boolean;
  isSupplier: boolean;
  arOutstanding: number;
  arOverdue: number;
};

export type XeroInvoiceFilter = 'all' | 'overdue' | 'paid' | 'outstanding' | 'unpaid' | 'draft';

export type XeroInvoiceQuery = {
  filter?: XeroInvoiceFilter;
  from?: string;
  to?: string;
  search?: string;
  contactId?: string;
  minDue?: number;
  maxDue?: number;
};

export type XeroArReport = {
  totalOutstanding: number;
  totalOverdue: number;
  overdueCount: number;
  unpaidCount: number;
  paidThisMonthTotal: number;
  paidThisMonthCount: number;
  aging: { current: number; d1_30: number; d31_60: number; d61_90: number; d90plus: number };
  topDebtors: Array<{ name: string; overdue: number; outstanding: number; count: number }>;
};

export type XeroReminderLog = { sent_at: string; recipient: string; subject: string };

export type XeroCorrespondenceEntry = {
  id: string;
  xero_invoice_ids: string;
  contact_name: string | null;
  direction: 'out' | 'in' | 'note';
  recipient: string | null;
  reply_to: string | null;
  subject: string;
  body: string;
  stripe_payment_url: string | null;
  attachment_count: number;
  created_at: string;
};

export type SendReminderOptions = {
  to?: string;
  subject?: string;
  message?: string;
  attachPdf?: boolean;
  includeStripePaymentLink?: boolean;
};

function parseXeroDate(s?: string | null): number {
  if (!s) {
    return 0;
  }
  const m = /\/Date\((\d+)/.exec(s);
  if (m) {
    return parseInt(m[1], 10);
  }
  return new Date(s).getTime();
}

export function formatXeroDate(raw: string | null): string {
  if (!raw) {
    return '—';
  }
  const ms = parseXeroDate(raw);
  if (!ms) {
    return '—';
  }
  return new Date(ms).toLocaleDateString(undefined, { dateStyle: 'medium' });
}

export function fmtXeroMoney(n: number, cur = 'AUD'): string {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: cur.toUpperCase() }).format(n);
  } catch {
    return `${n.toFixed(2)} ${cur}`;
  }
}

function buildQuery(params: Record<string, string | number | undefined>): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') {
      q.set(k, String(v));
    }
  }
  const s = q.toString();
  return s ? `?${s}` : '';
}

export async function fetchXeroStatus(): Promise<{ connected: boolean }> {
  return apiFetch('/api/xero/status');
}

export async function fetchXeroInvoices(_settings: AppSettings, query: XeroInvoiceQuery = {}): Promise<XeroInvoiceRow[]> {
  if (useBackendAuth) {
    const r = await apiFetch<{ invoices: XeroInvoiceRow[] }>(
      `/api/xero/invoices${buildQuery({
        filter: query.filter || 'all',
        from: query.from,
        to: query.to,
        search: query.search,
        contactId: query.contactId,
        minDue: query.minDue,
        maxDue: query.maxDue,
      })}`
    );
    return r.invoices;
  }
  const { fetchXeroInvoices: legacy } = await import('./xeroInvoicesLegacy');
  return legacy(_settings, query.filter || 'all');
}

export async function fetchXeroInvoiceDetail(invoiceId: string): Promise<{
  invoice: XeroInvoiceDetail;
  reminders: XeroReminderLog[];
  correspondence: XeroCorrespondenceEntry[];
}> {
  return apiFetch(`/api/xero/invoices/${encodeURIComponent(invoiceId)}`);
}

export async function fetchXeroContacts(search?: string): Promise<XeroContactRow[]> {
  const r = await apiFetch<{ contacts: XeroContactRow[] }>(
    `/api/xero/contacts${search ? buildQuery({ search }) : ''}`
  );
  return r.contacts;
}

export async function fetchXeroReport(query?: { from?: string; to?: string }): Promise<XeroArReport> {
  return apiFetch(`/api/xero/reports${buildQuery({ filter: 'all', from: query?.from, to: query?.to })}`);
}

export async function sendXeroInvoiceReminder(
  invoiceId: string,
  body: SendReminderOptions
): Promise<{ ok: boolean; sentAt: string; recipient: string; stripePaymentUrl?: string }> {
  return apiFetch(`/api/xero/invoices/${encodeURIComponent(invoiceId)}/remind`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function sendXeroBatchReminder(
  invoiceIds: string[],
  body: SendReminderOptions
): Promise<{ ok: boolean; sentAt: string; recipient: string; stripePaymentUrl?: string }> {
  return apiFetch('/api/xero/invoices/remind-batch', {
    method: 'POST',
    body: JSON.stringify({ invoiceIds, ...body }),
  });
}

export async function addXeroCorrespondenceNote(
  invoiceId: string,
  body: string,
  subject?: string
): Promise<void> {
  await apiFetch(`/api/xero/invoices/${encodeURIComponent(invoiceId)}/correspondence/note`, {
    method: 'POST',
    body: JSON.stringify({ body, subject }),
  });
}

export function xeroAuthorizeUrl(): string {
  return apiUrl('/api/xero/authorize');
}
